import { useAuth } from '../hooks/useAuth';
import {
  Outlet,
  Navigate,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import DashboardNavLink from '../components/DashboardNavLink';
import logo from '../assets/atad-logo-vertical-white.png';
import customerServiceIcon from '../assets/customer-service.png';
import { getEmailUsername, isAnyAdmin, isAdmin, canUseActivations } from '../utils';
import LogoutLink from '../components/LogoutLink';
import { getUserInitial } from '../utils';
import ScheduledMaintenanceBanner from '../components/ScheduledMaintenanceBanner';
import LoadingOverlay from '../components/LoadingOverlay';
import { useEffect, useState } from 'react';
import { useTimeoutFn } from 'react-use';
import { UPDATE_SUBSCRIPTION_FROM_STRIPE } from '../mutations';
import { useMutation, useQuery } from '@apollo/client';
import { USER_STATUS_INDICATORS_QUERY } from '../queries';
import { MdBolt, MdPeople, MdMenu, MdClose } from 'react-icons/md';
import { FaRegAddressBook, FaBriefcase, FaRegLightbulb } from 'react-icons/fa';
import { TbChartCandle } from 'react-icons/tb';

export default function DashboardRoutes() {
  const { user, getUser, updateUserPlan } = useAuth();
  const userInitial = getUserInitial(user);
  const plan = user?.plan;
  const navigate = useNavigate();

  const [attemptingGetUser, setAttemptingGetUser] = useState(true);
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
    const saved = localStorage.getItem('navCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [
    showValidatingSubscriptionOverlay,
    setShowValidatingSubscriptionOverlay,
  ] = useState(false);
  const [subscriptionPollingTimeout, setSubscriptionPollingTimeout] =
    useState(null);

  const toggleNavCollapse = () => {
    setIsNavCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem('navCollapsed', JSON.stringify(newState));
      return newState;
    });
  };

  useEffect(() => {
    getUser().finally(() => setAttemptingGetUser(false));
  }, [getUser]);

  const { data, refetch: refetchStatusIndicators } = useQuery(
    USER_STATUS_INDICATORS_QUERY,
    {
      pollInterval: 10000,
      fetchPolicy: 'network-only',
    }
  );
  const userStatusIndicators = data?.userStatusIndicators;

  const [updateSubscriptionFromStripe] = useMutation(
    UPDATE_SUBSCRIPTION_FROM_STRIPE,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => refetchStatusIndicators(),
    }
  );

  useTimeoutFn(() => {
    if (subscriptionPollingTimeout != null) {
      updateSubscriptionFromStripe();
      setShowValidatingSubscriptionOverlay(false);
    }
  }, subscriptionPollingTimeout);

  useEffect(() => {
    if (userStatusIndicators) {
      const {
        currentPlan,
        hasFailedPayment,
        subscriptionErrors,
        currentRecurringInterval,
      } = userStatusIndicators;

      if (currentPlan != null && plan != null) {
        if (plan !== currentPlan) {
          updateUserPlan(currentPlan);
        }
      }

      if (hasFailedPayment && subscriptionErrors?.length > 0) {
        let targetPlan = currentPlan;

        const invalidPlanError = subscriptionErrors?.find(
          ({ code }) => code === 'invalid-plan'
        );
        if (invalidPlanError) {
          const { data } = invalidPlanError;
          targetPlan = data?.actual;
        }

        navigate(`profile/subscription/order`, {
          state: {
            orderDetails: {
              plan: targetPlan,
              recurringInterval: currentRecurringInterval || 'monthly',
              retryingFailedPayment: true,
            },
          },
        });
        return;
      }

      const invalidStatusError = subscriptionErrors?.find(
        ({ code }) => code === 'invalid-status'
      );

      if (invalidStatusError) {
        const { data } = invalidStatusError;

        const subscriptionStatus = data?.actual;

        if (subscriptionStatus === 'incomplete') {
          setShowValidatingSubscriptionOverlay(true);
          setSubscriptionPollingTimeout(10000);
          return;
        }
      }

      setShowValidatingSubscriptionOverlay(false);
    }
  }, [navigate, plan, updateUserPlan, userStatusIndicators]);

  const location = useLocation();

  if (attemptingGetUser) {
    return <LoadingOverlay />;
  }

  if (user) {
    if (isAnyAdmin(user)) {
      return <Navigate to="/admin" />;
    }
    return (
      <div className="flex h-screen w-screen overflow-hidden">
        <div className={`bg-background-dark h-full flex flex-col flex-shrink-0 items-center justify-between pt-4 overflow-y-auto overflow-x-hidden transition-all duration-300 ${
          isNavCollapsed ? 'w-16' : 'w-44'
        }`}>
          <div className="flex flex-col items-center w-full">
            <button
              onClick={toggleNavCollapse}
              className="text-white hover:text-primary mb-4 p-2 rounded-lg hover:bg-gray-700 transition-colors"
              aria-label="Toggle navigation"
            >
              {isNavCollapsed ? <MdMenu size={20} /> : <MdClose size={20} />}
            </button>
            <Link to="/dashboard" className="mb-8">
              <img className={`transition-all duration-300 ${
                isNavCollapsed ? 'w-10' : 'w-20'
              }`} src={logo} alt="logo" />
            </Link>
            <DashboardNavLink
              to="audiences/audiencebuilder"
              text="Audiences"
              icon={<MdPeople />}
              collapsed={isNavCollapsed}
            />
            {/* Activations is gated by the `activations-user` role
                (admins always pass). Hide the nav entry entirely for
                users who don't have it — ActivationBuilder also runs the
                same check and falls back to a PermissionModal if the
                route is hit directly. */}
            {canUseActivations(user) && (
              <DashboardNavLink
                to="activations/activationbuilder"
                text="Activations"
                icon={<MdBolt />}
                collapsed={isNavCollapsed}
              />
            )}
            <DashboardNavLink
              to="enhancements"
              text="Enhancements"
              icon={<FaRegAddressBook />}
              collapsed={isNavCollapsed}
            />
            <DashboardNavLink
              to="insights"
              text="Insights"
              icon={<TbChartCandle />}
              collapsed={isNavCollapsed}
            />
            <DashboardNavLink
              to="ask-addy"
              text="Ask Addy"
              icon={<FaRegLightbulb />}
              collapsed={isNavCollapsed}
            />
            <DashboardNavLink
              to="mywork/orders"
              text="My Work"
              icon={<FaBriefcase />}
              collapsed={isNavCollapsed}
            />
          </div>
          <ScheduledMaintenanceBanner />
          <div className="flex flex-col items-center pb-4 w-full">
            <a 
              href="https://docs.google.com/forms/d/e/1FAIpQLSc96r5JHLuGAAHvFABrO8bntkyAU8T2tOb6aw4C4tonHLNPOQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className={`mb-3 hover:opacity-80 transition-opacity ${
                isNavCollapsed ? 'mx-auto' : ''
              }`}
              title="Customer Service"
            >
              <img 
                src={customerServiceIcon} 
                alt="Customer Service" 
                className={`filter invert transition-all duration-300 ${
                  isNavCollapsed ? 'w-6 h-6' : 'w-6 h-6'
                }`}
              />
            </a>
            <DashboardNavLink to="profile" collapsed={isNavCollapsed}>
              <div className={`flex ${isNavCollapsed ? 'justify-center' : 'flex-col'} items-center`}>
                <div className={`nav-link overflow-hidden rounded-full flex items-center justify-center bg-white border-2 border-background transition-all duration-300 ${
                  isNavCollapsed ? 'h-10 w-10 text-lg' : 'h-12 w-12 text-2xl'
                }`}>
                  {user.avatar ? (
                    <img
                      className="object-cover object-center h-full w-full"
                      src={user.avatar}
                      alt="user avatar"
                    />
                  ) : (
                    userInitial
                  )}
                </div>
                {!isNavCollapsed && (
                  <div className="text-white text-xs pt-1 px-1 text-center">
                    {user.firstName ||
                      getEmailUsername(user?.email) ||
                      userInitial}
                  </div>
                )}
              </div>
            </DashboardNavLink>
            {!isNavCollapsed && <LogoutLink className="pt-4 text-sm" />}
          </div>
        </div>
        <div className="flex flex-col flex-grow h-screen overflow-hidden">
          <div className="flex-grow overflow-auto">
            <Outlet />
          </div>
          {showValidatingSubscriptionOverlay && (
            <LoadingOverlay message="Validating your subscription..." />
          )}
        </div>
      </div>
    );
  }

  return <Navigate to="/signin" state={{ from: location }} />;
}
